import { useRef } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import { Icon } from "../../Icon/Icon";
import { IconButton } from "../../primitives/IconButton";
import { TextField, rowHeight } from "../../primitives/TextField";
import { useDisclosure } from "../../primitives/useDisclosure";
import type { UiTheme } from "../../theme";

// PE-31b — the program's NAME and DESCRIPTION, the two editable rows of the
// locked three-row header (program-editor-redesign.md §PE-27).
//
// ⭐⭐ THE DESCRIPTION IS FOLDED, and that is the whole reason this file exists.
// Antenore's smoke of `4.36.0-dev`: «in uno dei programmi la descrizione è
// lunga e non posso scrollare perché il text form ruba il focus» ⇒ «descrizione
// dovrebbe essere piccola di base ed espandibile».
// ⚠️ The mechanism behind what he saw: a multiline `<TextInput>` with no height
// cap GROWS to its content, and a drag inside it scrolls ITS text, not the page
// under it. A long description therefore became a tall wall that also ate the
// page's scroll gesture — the surface was unreachable, not merely ugly.
// ⇒ Folded, the description is a `<Text>`: there is no TextInput in the tree at
// all, so it cannot take a gesture it was never given. Unfolded, it is a real
// field with a HEIGHT CAP: past six rows it scrolls inside itself, and the page
// keeps its own scroll everywhere around it.
//
// ⛔ The fold is `useDisclosure`, the repo's shared collapse/expand state — not
// a second hand-rolled `useState` + toggle + accessibilityState triple, which is
// exactly the duplication that hook was extracted to end (CN-disclosure).
export type PrescribeHeaderFieldsProps = {
  theme: UiTheme;
  name: string;
  onChangeName: (v: string) => void;
  description: string;
  onChangeDescription: (v: string) => void;
  /** The fold's starting state — `useDisclosure`'s own `initial`, surfaced,
   *  and nothing more exotic than that. ⛔ Defaults to FOLDED and the app
   *  passes nothing: a description opens small, which is the whole point of the
   *  fold. The visual bench passes `true` because a scene cannot tap and the
   *  open state still has to be photographed. */
  defaultExpanded?: boolean;
};

export function PrescribeHeaderFields({
  theme,
  name,
  onChangeName,
  description,
  onChangeDescription,
  defaultExpanded = false,
}: PrescribeHeaderFieldsProps) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  const desc = useDisclosure(defaultExpanded);
  // ⛔ The caret follows a TAP, never a fold that was already open when the
  // component mounted. Measured at the bench, which opens it via
  // `defaultExpanded`: `autoFocus` fired on load and dragged the viewport to
  // the field, so the scene photographed blank. Same rule would apply to any
  // future caller that opens this programmatically — a component that opens
  // itself has not asked for the keyboard.
  const openedByTap = useRef(false);

  const descLabel = t("program.description", { defaultValue: "Description" });
  const descPlaceholder = t("program.descriptionPlaceholder", {
    defaultValue:
      "What this cycle is for, who it's aimed at, any notes for the athletes.",
  });

  return (
    <View style={styles.wrap}>
      {/* ⛔ The name field carries the 44 floor at the CALLSITE, not in
          TextField: 36 is right for the forty dense forms that primitive
          serves, and wrong for the first control a thumb meets on a screen. */}
      <TextField
        theme={theme}
        value={name}
        onChangeText={onChangeName}
        placeholder={t("program.namePlaceholder", {
          defaultValue: "e.g. Powerbuilding 6-week",
        })}
        accessibilityLabel={t("program.name", { defaultValue: "Name" })}
        style={styles.nameField}
      />

      {desc.expanded ? (
        <View style={styles.descRow}>
          <TextField
            theme={theme}
            value={description}
            onChangeText={onChangeDescription}
            placeholder={descPlaceholder}
            accessibilityLabel={descLabel}
            multiline
            minRows={3}
            // ⭐ The tap that opened this row meant «fammi scrivere», so the
            // caret belongs here already — a second tap to start typing is a
            // tap that exists only because the code forgot (a11y review).
            autoFocus={openedByTap.current}
            // The cap that gives the PAGE its scroll back. Token-derived, so it
            // tracks the type scale instead of pinning a magic pixel height.
            style={styles.descField}
          />
          <IconButton
            theme={theme}
            name="chevron-up"
            variant="cancel"
            onPress={desc.toggle}
            accessibilityLabel={t("program.descriptionCollapse", {
              defaultValue: "Collapse the description",
            })}
            {...desc.a11yProps}
          />
        </View>
      ) : (
        // ⭐ TWO LINES at most, and a whole row of it: the target is the strip,
        // not the chevron — «espandibile per comfort du tocco», his words.
        // ⛔ Two and not one: at one line a real description showed a handful of
        // words to someone who cannot lean in, and «piccola di base» is about
        // not building a wall, not about spending the fewest pixels possible.
        // The a11y floor wins over compactness (prompt.md §DESIGN). A short
        // description still takes one line — the row grows to its content above
        // the 44 floor instead of reserving two.
        // ⛔ An empty description shows the placeholder in the dim ink, so the
        // row still reads as "there is something to write here" rather than as
        // a blank nobody can act on.
        <Pressable
          onPress={() => {
            openedByTap.current = true;
            desc.toggle();
          }}
          accessibilityRole="button"
          accessibilityLabel={
            description.trim() === ""
              ? t("program.descriptionAdd", { defaultValue: "Add a description" })
              : `${descLabel}: ${description}`
          }
          accessibilityHint={t("program.descriptionExpand", {
            defaultValue: "Opens the description for editing",
          })}
          {...desc.a11yProps}
          style={({ pressed }) => [
            styles.descFolded,
            pressed && styles.descFoldedPressed,
          ]}
        >
          <Text
            style={[
              styles.descPreview,
              description.trim() === "" && styles.descPreviewEmpty,
            ]}
            numberOfLines={2}
          >
            {description.trim() === "" ? descPlaceholder : description}
          </Text>
          <Icon
            name="chevron-down"
            size={theme.typography.sizes.md}
            color={theme.palette.ink3}
          />
        </Pressable>
      )}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  // ⛔ TextField's own row unit, imported, not re-derived: the cap has to count
  // rows in the same unit the box grows in, and two copies of that arithmetic
  // drift the day the type scale moves (review finding).
  const row = rowHeight(theme);
  return StyleSheet.create({
    wrap: { paddingHorizontal: s.s5, paddingTop: s.s3, gap: s.s2 },
    nameField: {
      fontSize: ty.sizes.lg,
      fontWeight: "700",
      minHeight: 44,
    },
    descRow: { flexDirection: "row", alignItems: "flex-start", gap: s.s2 },
    // Six rows, then it scrolls inside itself instead of pushing the card off
    // the screen. Without this the field grew without bound and swallowed the
    // page's scroll — the defect this fold exists to cure.
    descField: { flex: 1, maxHeight: row * 6 + s.s3 },
    descFolded: {
      flexDirection: "row",
      alignItems: "center",
      gap: s.s2,
      minHeight: 44,
      paddingHorizontal: s.s3,
      borderRadius: r.r3,
      borderWidth: 1,
      borderColor: p.border,
      backgroundColor: p.surface,
    },
    descFoldedPressed: { backgroundColor: p.surface2 },
    descPreview: { flex: 1, color: p.ink, fontSize: ty.sizes.base },
    descPreviewEmpty: { color: p.ink3 },
  });
}
